import { useState, useMemo } from "react";
import axios from "axios";
import { Marker } from "react-leaflet";
import { toast } from "sonner";
import { Store, Crosshair } from "lucide-react";
import { WomaMap } from "./WomaMap";
import { roleIcon } from "./roleMarkers";
import { useGeolocation } from "@/hooks/useGeolocation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Commerçant : position FIXE de la boutique (déplaçable), utilisée pour les commandes locales
export const PartnerMapPanel = ({ user, onUserUpdate }) => {
  const { position, status, request } = useGeolocation();
  const shopLoc = user?.shop_location || (user?.latitude && user?.longitude ? { latitude: user.latitude, longitude: user.longitude } : null);
  const saved = shopLoc ? { lat: shopLoc.latitude, lng: shopLoc.longitude } : null;
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const shop = draft || saved || position;
  const icon = useMemo(() => roleIcon("partner", { size: 46 }), []);

  const save = async () => {
    if (!shop) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/users/me/location`, { latitude: shop.lat, longitude: shop.lng, role: "partner" });
      onUserUpdate?.({ ...user, ...r.data.user });
      localStorage.setItem("user", JSON.stringify({ ...user, ...r.data.user }));
      setDraft(null);
      toast.success("Position de la boutique enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-3" data-testid="partner-map-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#D4AF37]">Ma boutique sur la carte</p>
          <p className="text-[11px] text-slate-400 mt-1" data-testid="partner-map-status">
            {saved && !draft ? "Position fixée · visible pour les commandes locales" : "Déplacez le marqueur doré sur votre boutique puis enregistrez"}
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => position ? setDraft(position) : request()} className="rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 transition-colors" data-testid="partner-map-use-gps-btn">
            <Crosshair size={14} /> Ma position
          </button>
          <button type="button" onClick={save} disabled={!shop || saving || (saved && !draft)} className="rounded-full bg-[#D4AF37] disabled:opacity-40 text-[#0F172A] px-4 py-2 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 active:scale-95 transition-transform" data-testid="partner-map-save-btn">
            <Store size={14} /> {saving ? "..." : "Fixer ma boutique"}
          </button>
        </div>
      </div>
      <WomaMap self={position} role="dot" status={saved ? null : status} onRequest={request} testId="partner-map">
        {shop && (
          <Marker
            position={[shop.lat, shop.lng]}
            icon={icon}
            draggable
            zIndexOffset={1000}
            eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); setDraft({ lat: p.lat, lng: p.lng }); } }}
          />
        )}
      </WomaMap>
    </section>
  );
};
